import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { useConfirm } from "@/components/admin/ConfirmDialog";

export const Route = createFileRoute("/_authenticated/admin/categorias")({
  component: AdminCategories,
});

type Row = { id: string; name: string; slug: string; description: string | null; sort_order: number };

function slugify(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function AdminCategories() {
  const [rows, setRows] = useState<Row[]>([]);
  const { confirm, confirmDialog } = useConfirm();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  async function load() {
    const { data } = await supabase.from("categories").select("*").order("sort_order");
    setRows((data ?? []) as unknown as Row[]);
  }

  useEffect(() => {
    void load();
  }, []);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    const { error } = await supabase
      .from("categories")
      .insert({ name, slug: slugify(name), description: description || null, sort_order: rows.length });
    if (error) return toast.error(error.message);
    toast.success("Categoria criada.");
    setName("");
    setDescription("");
    void load();
  }

  async function update(row: Row, patch: Partial<Row>) {
    const { error } = await supabase.from("categories").update(patch).eq("id", row.id);
    if (error) return toast.error(error.message);
    void load();
  }

  async function remove(row: Row) {
    const ok = await confirm({
      title: "Excluir categoria?",
      description: `A categoria "${row.name}" será removida e os artigos ligados a ela ficarão sem categoria.`,
      confirmLabel: "Excluir categoria",
    });
    if (!ok) return;
    const { error } = await supabase.from("categories").delete().eq("id", row.id);
    if (error) return toast.error(error.message);
    toast.success("Categoria excluída.");
    void load();
  }

  return (
    <div className="kk-cms__page">
      <div className="kk-cms__head">
        <div>
          <h1>Categorias</h1>
          <p>{rows.length} categoria{rows.length === 1 ? "" : "s"}</p>
        </div>
      </div>

      <form className="kk-cms__card kk-cms__filters" onSubmit={add}>
        <input type="text" placeholder="Nome da categoria" value={name} onChange={(e) => setName(e.target.value)} />
        <input type="text" placeholder="Descrição" value={description} onChange={(e) => setDescription(e.target.value)} />
        <button type="submit" className="kk-cms__btn">
          <Plus size={15} /> Adicionar
        </button>
      </form>

      <div className="kk-cms__table-wrap">
        <table className="kk-cms__table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Slug</th>
              <th>Descrição</th>
              <th style={{ textAlign: "right" }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>
                  <input type="text" defaultValue={r.name} onBlur={(e) => update(r, { name: e.target.value })} />
                </td>
                <td>
                  <input type="text" defaultValue={r.slug} onBlur={(e) => update(r, { slug: slugify(e.target.value) })} />
                </td>
                <td>
                  <input type="text" defaultValue={r.description ?? ""} onBlur={(e) => update(r, { description: e.target.value })} />
                </td>
                <td>
                  <div className="kk-cms__row-actions">
                    <button type="button" className="kk-icon-btn kk-icon-btn--danger" title="Excluir" onClick={() => remove(r)}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {confirmDialog}
    </div>
  );
}
